import React from 'react';
import { motion } from 'framer-motion';
import { 
  Flame, 
  Split, 
  ShoppingBag, 
  Database, 
  Cpu, 
  Sparkles
} from 'lucide-react';

const EQUATION_ITEMS = [
  { id: 'traffic', label: 'TRAFFIC', icon: Flame, color: '#ec2b28', tag: 'Paid & Organic Acquisition' },
  { id: 'conversion', label: 'CONVERSION', icon: Split, color: '#f97316', tag: 'CRO & Store Architecture' },
  { id: 'commerce', label: 'COMMERCE', icon: ShoppingBag, color: '#10b981', tag: 'Marketplaces & Retention' },
  { id: 'data', label: 'DATA', icon: Database, color: '#3b82f6', tag: 'Attribution & Telemetry' },
  { id: 'operations', label: 'OPERATIONS', icon: Cpu, color: '#a855f7', tag: 'AI Workflows & Automations' },
];

const PAIN_POINTS = [
  {
    icon: Flame,
    color: '#ec2b28',
    title: 'The Acquisition Trap',
    text: "Maybe you're driving high-volume traffic through ads, but leaky funnels and checkout friction prevent users from converting.",
  },
  {
    icon: Database,
    color: '#3b82f6',
    title: 'Fragmented Blindspots',
    text: 'Maybe your data exists across ten different disconnected platforms, leaving leadership guessing where profit is actually made.',
  },
  {
    icon: ShoppingBag,
    color: '#10b981',
    title: 'Isolated Distribution',
    text: "Maybe your products perform on your website, but aren't reaching high-intent buyers on the marketplaces and quick commerce networks where they already shop.",
  },
  {
    icon: Cpu,
    color: '#a855f7',
    title: 'Operational Drag',
    text: 'Maybe your team is spending hundreds of hours doing manual reporting and repetitive workflows that should be completely autonomous with modern AI.',
  },
];

export default function TheProblem() {
  return (
    <section className="prob-section" id="the-problem">
      <style>{`
        .prob-section {
          position: relative;
          background: #07070a;
          color: #ffffff;
          font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
          padding: clamp(80px, 12vh, 140px) clamp(20px, 6vw, 90px);
          overflow: hidden;
          border-top: 1px solid rgba(255, 255, 255, 0.07);
          border-bottom: 1px solid rgba(255, 255, 255, 0.07);
        }

        /* Ambient Glow */
        .prob-bg-glow {
          position: absolute;
          top: 30%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: clamp(340px, 75vw, 1000px);
          height: clamp(300px, 45vw, 600px);
          background: radial-gradient(circle, rgba(236, 43, 40, 0.07) 0%, rgba(59, 130, 246, 0.03) 50%, transparent 70%);
          filter: blur(100px);
          pointer-events: none;
          z-index: 0;
        }

        .prob-container {
          position: relative;
          max-width: 1300px;
          margin: 0 auto;
          z-index: 1;
        }

        /* ── Header ── */
        .prob-header {
          text-align: center;
          max-width: 920px;
          margin: 0 auto clamp(48px, 8vh, 80px);
        }

        .prob-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          border-radius: 999px;
          background: rgba(236, 43, 40, 0.1);
          border: 1px solid rgba(236, 43, 40, 0.3);
          color: #ec2b28;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          margin-bottom: 24px;
        }

        .prob-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #ec2b28;
          box-shadow: 0 0 8px #ec2b28;
        }

        .prob-title {
          font-size: clamp(2.2rem, 5vw, 4.2rem);
          font-weight: 900;
          line-height: 1.1;
          letter-spacing: -0.04em;
          color: #ffffff;
          margin: 0 0 20px 0;
          text-wrap: balance;
        }

        .prob-title-red {
          color: #ec2b28;
        }

        .prob-lead {
          font-size: clamp(1rem, 1.4vw, 1.25rem);
          font-weight: 500;
          line-height: 1.6;
          color: #d1d5db;
          margin: 0 auto 12px;
          max-width: 780px;
        }

        .prob-lead-strong {
          color: #ffffff;
          font-weight: 700;
        }

        .prob-subtext {
          font-size: clamp(0.85rem, 1.05vw, 0.96rem);
          font-weight: 400;
          line-height: 1.7;
          color: #9ca3af;
          max-width: 700px;
          margin: 0 auto;
        }

        /* ── Equation Visual Bar ── */
        .prob-equation-wrap {
          background: linear-gradient(180deg, #111116 0%, #0a0a0e 100%);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 28px;
          padding: clamp(28px, 4vw, 44px) clamp(20px, 3vw, 36px);
          margin-bottom: clamp(48px, 7vh, 72px);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        .prob-equation-label {
          text-align: center;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.4);
          margin-bottom: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .prob-equation-nodes {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: clamp(8px, 1.6vw, 20px);
          flex-wrap: wrap;
        }

        .prob-eq-node {
          display: flex;
          flex-direction: column;
          align-items: center;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 18px;
          padding: clamp(14px, 2vw, 22px) clamp(16px, 2.2vw, 26px);
          min-width: clamp(120px, 14vw, 170px);
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          box-sizing: border-box;
        }

        .prob-eq-node:hover {
          transform: translateY(-4px);
          background: rgba(255, 255, 255, 0.06);
          border-color: var(--node-color);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5), 0 0 20px var(--node-glow);
        }

        .prob-eq-icon-wrap {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          border: 1px solid;
          transition: transform 0.3s ease;
        }

        .prob-eq-node:hover .prob-eq-icon-wrap {
          transform: scale(1.1);
        }

        .prob-eq-label {
          font-size: clamp(0.82rem, 1vw, 0.95rem);
          font-weight: 800;
          letter-spacing: 0.08em;
          color: #ffffff;
          margin-bottom: 4px;
        }

        .prob-eq-tag {
          font-size: 0.65rem;
          font-weight: 600;
          color: #9ca3af;
          text-align: center;
          line-height: 1.3;
          white-space: nowrap;
        }

        .prob-eq-mult {
          font-size: clamp(1.2rem, 2vw, 1.8rem);
          font-weight: 900;
          color: rgba(255, 255, 255, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          user-select: none;
          padding: 0 4px;
        }

        /* ── 4 Disconnect Pain Point Cards ── */
        .prob-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          margin-bottom: clamp(48px, 8vh, 72px);
        }

        .prob-card {
          background: linear-gradient(170deg, #111116 0%, #0c0c10 100%);
          border: 1px solid rgba(255, 255, 255, 0.07);
          border-radius: 22px;
          padding: clamp(24px, 3vw, 34px);
          display: flex;
          gap: 20px;
          align-items: flex-start;
          transition: all 0.3s ease;
        }

        .prob-card:hover {
          border-color: rgba(255, 255, 255, 0.18);
          transform: translateY(-3px);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
        }

        .prob-card-icon-wrap {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border: 1px solid;
          margin-top: 2px;
        }

        .prob-card-title {
          font-size: clamp(1rem, 1.2vw, 1.18rem);
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 8px 0;
          letter-spacing: -0.01em;
        }

        .prob-card-text {
          font-size: clamp(0.82rem, 0.98vw, 0.92rem);
          color: #9ca3af;
          line-height: 1.65;
          margin: 0;
        }

        /* ── Solution / Manifesto Banner ── */
        .prob-manifesto {
          background: linear-gradient(135deg, rgba(236, 43, 40, 0.12) 0%, rgba(17, 17, 22, 0.95) 50%, rgba(59, 130, 246, 0.08) 100%);
          border: 1.5px solid rgba(236, 43, 40, 0.35);
          border-radius: 28px;
          padding: clamp(36px, 5vw, 56px) clamp(24px, 4vw, 52px);
          text-align: center;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.7), 0 0 30px rgba(236, 43, 40, 0.15);
        }

        .prob-manifesto-sub {
          font-size: 0.76rem;
          font-weight: 800;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #ec2b28;
          margin-bottom: 14px;
        }

        .prob-manifesto-heading {
          font-size: clamp(1.6rem, 3.2vw, 2.8rem);
          font-weight: 900;
          letter-spacing: -0.03em;
          color: #ffffff;
          line-height: 1.2;
          margin: 0 0 16px 0;
        }

        .prob-manifesto-heading span {
          color: #ec2b28;
        }

        .prob-manifesto-desc {
          font-size: clamp(0.92rem, 1.2vw, 1.05rem);
          color: #d1d5db;
          max-width: 760px;
          margin: 0 auto;
          line-height: 1.7;
        }

        @media (max-width: 1024px) {
          .prob-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .prob-section {
            padding-left: 16px;
            padding-right: 16px;
          }
          .prob-equation-nodes {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
          .prob-eq-mult {
            display: none;
          }
          .prob-eq-node {
            min-width: auto;
            width: 100%;
          }
          .prob-card {
            padding: 20px 16px;
          }
        }
      `}</style>

      <div className="prob-bg-glow" />

      <div className="prob-container">
        {/* Header */}
        <div className="prob-header">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="prob-eyebrow">
              <span className="prob-eyebrow-dot" />
              <span>THE PROBLEM</span>
            </div>

            <h2 className="prob-title">
              Your business isn't divided into services.<br />
              <span className="prob-title-red">Why should your partners be?</span>
            </h2>

            <p className="prob-lead">
              Most businesses don't have a “website problem” or a “marketing problem” in isolation. <span className="prob-lead-strong">They have a growth problem.</span>
            </p>

            <p className="prob-subtext">
              Hiring four fragmented agencies creates handoff gaps, misaligned KPIs, and endless finger-pointing. 
              The actual bottleneck almost always sits between departments, platforms, and systems.
            </p>
          </motion.div>
        </div>

        {/* ── Equation Bar: TRAFFIC × CONVERSION × COMMERCE × DATA × OPERATIONS ── */}
        <motion.div
          className="prob-equation-wrap"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="prob-equation-label">
            <Sparkles size={14} color="#ec2b28" />
            <span>The Compounding Growth Equation</span>
          </div>

          <div className="prob-equation-nodes">
            {EQUATION_ITEMS.map((item, idx) => {
              const IconComp = item.icon;
              return (
                <React.Fragment key={item.id}>
                  <div
                    className="prob-eq-node"
                    style={{
                      '--node-color': item.color,
                      '--node-glow': `${item.color}30`,
                    }}
                  >
                    <div
                      className="prob-eq-icon-wrap"
                      style={{
                        background: `${item.color}15`,
                        borderColor: `${item.color}35`,
                        color: item.color,
                      }}
                    >
                      <IconComp size={20} strokeWidth={2} />
                    </div>
                    <span className="prob-eq-label">{item.label}</span>
                    <span className="prob-eq-tag">{item.tag}</span>
                  </div>

                  {idx < EQUATION_ITEMS.length - 1 && (
                    <div className="prob-eq-mult">×</div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </motion.div>

        {/* ── 4 Disconnect Pain Point Cards ── */}
        <div className="prob-grid">
          {PAIN_POINTS.map((pt, i) => {
            const IconComponent = pt.icon;
            return (
              <motion.div
                key={pt.title}
                className="prob-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div
                  className="prob-card-icon-wrap"
                  style={{
                    background: `${pt.color}15`,
                    borderColor: `${pt.color}35`,
                    color: pt.color,
                  }}
                >
                  <IconComponent size={20} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="prob-card-title">{pt.title}</h3>
                  <p className="prob-card-text">{pt.text}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ── Solution / Manifesto Banner ── */}
        <motion.div
          className="prob-manifesto"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="prob-manifesto-sub">THE AURELIX ADVANTAGE</p>
          <h3 className="prob-manifesto-heading">
            Most growth problems don't belong to <span>one department</span>.
          </h3>
          <p className="prob-manifesto-desc">
            The problem sits between departments, platforms, and systems. That's where Aurelix comes in — bringing acquisition, conversion, data intelligence, and AI automation together under one connected roof.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
